import { createContext, useContext, useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { api } from "../../lib/api";
import { devError, devLog, devWarn } from "../../utils/devLog";

// Create context for auth state
export const AuthContext = createContext<any>(null);

// Custom hook to access auth state
export const useAuthStatus = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthStatus must be used within AuthProvider");
  }
  return context;
};

// Debug logging helper
const logAuthEvent = (
  event: string,
  data: Record<string, unknown> = {},
  level: "log" | "warn" | "error" = "log"
) => {
  const payload = {
    currentPath: typeof window !== "undefined" ? window.location.pathname : "",
    ...data,
  };

  if (level === "error") {
    devError("auth", event, payload);
    return;
  }

  if (level === "warn") {
    devWarn("auth", event, payload);
    return;
  }

  devLog("auth", event, payload);
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const { isSignedIn, user, isLoaded } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastUserId, setLastUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const isClubEnabled = import.meta.env.VITE_CLUB_ENABLED === "true";

  // Log state changes with more detail
  useEffect(() => {
    logAuthEvent("Auth State Change", {
      isSignedIn,
      isLoaded,
      userId: user?.id,
      isInitialized,
      isLoading,
      lastUserId,
      emailVerified: user?.emailAddresses?.[0]?.verification?.status,
      currentPath: window.location.pathname,
    });
  }, [isSignedIn, isLoaded, user, isInitialized, isLoading, lastUserId]);

  // Effect to handle user initialization
  useEffect(() => {
    const initializeUser = async () => {
      // Wait for Clerk to load
      if (!isLoaded) {
        logAuthEvent("Clerk Not Loaded");
        return;
      }

      // Reset loading state when user signs out
      if (!isSignedIn || !user) {
        logAuthEvent("No User", { isSignedIn, userId: user?.id });
        setIsLoading(false);
        setIsInitialized(true);
        return;
      }

      // Prevent duplicate initialization
      if (isInitialized && lastUserId === user.id) {
        logAuthEvent("Already Initialized", { userId: user.id });
        return;
      }

      logAuthEvent("Starting Initialization", {
        userId: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        isInitialized,
        lastUserId,
      });

      try {
        let derivedIsAdmin = false;

        if (isClubEnabled) {
          try {
            const meResponse = await api.get("/club/me");
            derivedIsAdmin = Boolean(meResponse?.data?.member?.isAdmin ?? derivedIsAdmin);
            logAuthEvent("Club member found", {
              userId: user.id,
              isAdmin: derivedIsAdmin,
            });
          } catch (error: any) {
            if (error.response?.status !== 404) throw error;
            logAuthEvent("Club member not found, creating bootstrap record", { userId: user.id });

            try {
              const bootstrapPayload = {
                email: user.primaryEmailAddress?.emailAddress || "",
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                displayName: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
              };

              if (!bootstrapPayload.lastName) {
                logAuthEvent(
                  "agency_bootstrap_missing_last_name_fallback",
                  {
                    userId: user.id,
                    email: bootstrapPayload.email,
                    firstName: bootstrapPayload.firstName,
                    displayName: bootstrapPayload.displayName,
                    fallbackBehavior: "submit_empty_last_name_for_server_bootstrap",
                  },
                  "warn"
                );
              }

              logAuthEvent("agency_bootstrap_request_started", {
                userId: user.id,
                email: bootstrapPayload.email,
                hasFirstName: Boolean(bootstrapPayload.firstName),
                hasLastName: Boolean(bootstrapPayload.lastName),
                hasDisplayName: Boolean(bootstrapPayload.displayName),
              });

              const bootstrapResponse = await api.post("/club/me/bootstrap", bootstrapPayload);

              derivedIsAdmin = Boolean(
                bootstrapResponse?.data?.member?.isAdmin ?? derivedIsAdmin
              );

              logAuthEvent("agency_bootstrap_request_succeeded", {
                userId: user.id,
                isAdmin: derivedIsAdmin,
                memberId: bootstrapResponse?.data?.member?.id ?? null,
                memberStatus: bootstrapResponse?.data?.member?.status ?? null,
              });
            } catch (bootstrapError: any) {
              logAuthEvent(
                "agency_bootstrap_request_failed",
                {
                  userId: user.id,
                  error: {
                    message: bootstrapError.message,
                    response: bootstrapError.response?.data,
                    status: bootstrapError.response?.status,
                  },
                },
                "error"
              );
            }
          }
        }

        setIsAdmin(derivedIsAdmin);
        setLastUserId(user.id);
      } catch (error: any) {
        logAuthEvent(
          "Initialization Error",
          {
            userId: user.id,
            error: {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
            },
          },
          "error"
        );
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeUser();
  }, [isSignedIn, user, isLoaded, isInitialized, lastUserId, isClubEnabled]);

  // Reset initialization when user changes
  useEffect(() => {
    if (!user?.id || user.id === lastUserId) return;

    logAuthEvent("User Changed", {
      newUserId: user.id,
      lastUserId,
      isInitialized,
    });

    setIsInitialized(false);
    setIsLoading(true);
  }, [user?.id, lastUserId]);

  const value = {
    isLoading: isLoading || !isLoaded,
    isLoaded,
    isInitialized,
    isSignedIn,
    user: isSignedIn ? user : null,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
